import { ModuleField } from '../types';

const isEmpty = (v: unknown) => v === null || v === undefined || v === '';

export const getRecordTitle = (
  row: Record<string, unknown>,
  fields: ModuleField[],
  fallback = 'Record',
): string => {
  const priority = [
    'clientName', 'helperName', 'driverName', 'name', 'branchName',
    'vehicleNumber', 'tripNumber', 'invoiceNumber', 'biltyNumber', 'serviceType',
  ];
  for (const key of priority) {
    if (row[key]) return String(row[key]);
  }
  const first = fields.find((f) => row[f.name] != null && row[f.name] !== '');
  if (first) return String(row[first.name]);
  return fallback;
};

export const resolveFieldValue = (
  row: Record<string, unknown>,
  field: ModuleField,
): unknown => {
  if (field.type === 'client') {
    const client = row.client as Record<string, unknown> | string | undefined;
    if (typeof client === 'object' && client) {
      return client.clientName || client.companyFullName || client._id;
    }
    return row.clientName ?? row[field.name];
  }
  if (field.type === 'vehicle') {
    const vehicle = row.vehicle as Record<string, unknown> | string | undefined;
    if (typeof vehicle === 'object' && vehicle) return vehicle.vehicleNumber || vehicle._id;
    return row.vehicleNumber ?? row[field.name];
  }
  if (field.type === 'user') {
    const employee = row.employee as Record<string, unknown> | string | undefined;
    if (typeof employee === 'object' && employee) return employee.name || employee.email || employee._id;
    return row.employeeName ?? row[field.name];
  }
  // Always show Make / Model / Type names — never raw Mongo ids
  if (field.name === 'typeId' || field.type === 'vehicle_type') {
    return row.vehicleType || row.typeName || row[field.name];
  }
  if (field.name === 'makeId' || field.type === 'vehicle_make') {
    return row.brand || row.makeName || row[field.name];
  }
  if (field.name === 'modelId' || field.type === 'vehicle_model') {
    return row.vehicleModel || row.modelName || row[field.name];
  }
  if (field.name === 'brand') {
    const v = row.brand || row.makeName;
    if (v && !/^[a-f0-9]{24}$/i.test(String(v))) return v;
    return row.makeName || v;
  }
  if (field.name === 'vehicleModel') {
    const v = row.vehicleModel || row.modelName;
    if (v && !/^[a-f0-9]{24}$/i.test(String(v))) return v;
    return row.modelName || v;
  }
  if (field.name === 'vehicleType') {
    const v = row.vehicleType || row.typeName;
    if (v && !/^[a-f0-9]{24}$/i.test(String(v))) return v;
    return row.typeName || v;
  }
  return row[field.name];
};

export const formatFieldDisplay = (
  value: unknown,
  field: ModuleField,
  formatters?: {
    formatDate?: (d: Date | string, opts?: Intl.DateTimeFormatOptions) => string;
    formatCurrency?: (n: number) => string;
  },
): string => {
  if (isEmpty(value)) return '—';
  // Guard: never show bare ObjectIds in UI
  if (/^[a-f0-9]{24}$/i.test(String(value).trim())
    && (field.name === 'makeId' || field.name === 'modelId' || field.name === 'typeId'
      || field.name === 'brand' || field.name === 'vehicleModel' || field.name === 'vehicleType'
      || field.type === 'vehicle_make' || field.type === 'vehicle_model' || field.type === 'vehicle_type')) {
    return '—';
  }
  if (field.type === 'image' || field.type === 'file') return String(value);
  if (field.type === 'date') {
    return formatters?.formatDate
      ? formatters.formatDate(String(value), { dateStyle: 'medium' })
      : new Date(String(value)).toLocaleDateString();
  }
  if (field.type === 'number' && field.label.toLowerCase().includes('amount')) {
    const n = Number(value);
    return formatters?.formatCurrency ? formatters.formatCurrency(n) : String(n);
  }
  if (field.type === 'select' && field.options) {
    const match = field.options.find((o) => o.value === value);
    return match?.label || String(value).replace(/_/g, ' ');
  }
  if (field.name === 'ownerType') {
    const t = String(value || '');
    if (t === 'leased' || t === 'rented') return 'Vendor leased';
    if (t === 'owned') return 'Owned (Company)';
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
};

/** Build compact table columns — main fields only; full detail in drawer */
export const buildListColumns = (
  columns: { field: string; header: string; format?: 'date' | 'datetime' | 'currency' | 'number' }[],
  max = 4,
) => {
  const skip = new Set(['createdAt', 'updatedAt']);
  const statusCol = columns.find((c) => c.field === 'status' || c.field === 'paymentStatus');
  const main = columns.filter((c) => !skip.has(c.field) && c.field !== statusCol?.field).slice(0, max);
  return statusCol ? [...main, statusCol] : main;
};
